<template>
  <div class="k-directory-render">
    <div v-if="!readonly || directories.length" class="row between">
      <strong>{{ $tc('folder', 2) }}</strong>
      <a v-if="!readonly" href="#" @click.prevent="$emit('create')">{{ $t('create') }}</a>
    </div>
    <div class="k-directory-render__directory">
      <div v-for="(directory, index) in directories" :key="index + 'directory'">
        <div
          class="k-directory-render__directory-item"
          @click.self="navigate(directory)"
          ref="directory"
        >
          <FontAwesomeIcon
            @click="navigate(directory)"
            class="k-directory-render__directory-icon"
            :icon="['fal', 'folder']"
          />
          <div class="col mr-auto">
            <input
              v-if="directory.path === edit"
              v-model.lazy="editValue"
              class="k-directory-render__directory-name"
              ref="input"
              @blur="edit = null"
              @keypress.enter="$ev => $ev.target.blur()"
            />
            <div v-else class="k-directory-render__directory-name" @click="navigate(directory)">
              {{ getLastPath(directory.path) }}
            </div>
          </div>

          <AccessComponent
            v-if="accessGroups && accessGroups.length"
            :value="getAccess(directory, 'directory')"
            @input="$ev => onAccessChange($ev, directory, 'directory')"
            :groups="accessGroups"
          />

          <DropdownComponent v-if="!readonly" class="k-directory-render__directory-actions">
            <ButtonComponent icon="fa-pro-light:angle-down" />
            <template #dropdown>
              <ButtonComponent
                v-if="directory.path !== edit"
                @click="edit = directory.path"
                :label="i18n('rename')"
                icon="fa-pro-light:pen"
              />
              <ButtonComponent
                @click="$emit('remove', directory)"
                :label="i18n('delete')"
                icon="fa-pro-light:times"
              />
            </template>
          </DropdownComponent>
        </div>
      </div>

      <slot name="content-top" :item="target" />

      <FileComponent
        v-if="target && !readonly"
        ref="directories"
        :value="[]"
        @input="onDirectoryUpload"
        :upload-options="{ preTransform }"
        multiple
        accept="directory"
      >
        <template #drop-message>
          <span v-html="$t('dragFolderOrDrop')"></span>
        </template>
      </FileComponent>
    </div>
    <div
      v-if="!readonly || (target && target.files && target.files.length)"
      class="k-directory-render__files"
    >
      <strong class="mb-1">{{ $tc('file', 2) }}</strong>
      <FileComponent
        v-if="target"
        :value="target.files"
        @input="addFiles"
        multiple
        :readonly="readonly"
        :upload-options="uploadOptions"
      >
        <template #before-action="{ item }">
          <AccessComponent
            v-if="accessGroups && accessGroups.length"
            :value="getAccess(item, 'file')"
            @input="$ev => onAccessChange($ev, item, 'file')"
            :groups="accessGroups"
          />
        </template>
      </FileComponent>
    </div>
  </div>
</template>

<script>
import Icon from '@kvass/vue2-icon'
import { FileComponent } from '@kvass/file'
import { DropdownComponent } from 'vue-elder-dropdown'
import { ButtonComponent } from 'vue-elder-button'
import { EscapeFolderName, ExtractString, EscapeRegex } from './utils'
import AccessComponent from './Access.vue'

function escapeParentheses(value) {
  let reg = new RegExp(/(\(|\))/g)
  return value.replace(reg, '\\' + '$&').toString()
}

const Blacklist = ['.DS_Store']

export default {
  props: {
    value: {
      type: Array,
      required: true,
    },
    path: {
      type: String,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    uploadOptions: Object,
    accessGroups: Array,
  },

  data() {
    return {
      edit: null,
      directoryUploadMap: null,
    }
  },
  watch: {
    edit: {
      handler(val, oldVal) {
        if (!val || oldVal) return
        this.$nextTick(() => {
          this.$refs.input[0].select()
          this.$refs.input[0].focus()
        })
      },
    },
  },
  computed: {
    valueComp: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    editValue: {
      get() {
        if (!this.editTarget) return
        return this.getLastPath(this.editTarget.path)
      },
      set(val) {
        let edit = EscapeRegex(this.edit)
        let folderName = EscapeFolderName(val)
        let path = this.path ? `${this.path}/${folderName}` : folderName

        let targets = this.value.filter(
          v => new RegExp(`${edit}$`).test(v.path) || new RegExp(`${edit}/`).test(v.path),
        )

        targets.forEach(target => {
          target.path = target.path.replace(this.edit, path)
        })
      },
    },
    editTarget() {
      if (!this.edit) return {}
      return this.value.find(v => v.path === this.edit)
    },
    target() {
      return this.value
        .filter(v => v.path === this.path)
        .reduce(
          (res, cur) => {
            res.path = cur.path
            res.files = [...res.files, ...(cur.files || [])]
            return res
          },
          { path: '', files: [] },
        )
    },
    directories() {
      let pattern = new RegExp(
        `^${this.path ? escapeParentheses(this.path).split('/').join('\\/') + '\/' : ''}.+`,
      )
      let exists = []

      return this.value
        .filter(v => v.path.match(pattern))
        .map(v => {
          let path = v.path
            .split('/')
            .slice(0, this.path ? this.path.split('/').length + 1 : 1)
            .join('/')
          if (exists.includes(path)) return
          exists.push(path)
          return {
            ...v,
            path,
          }
        })
        .filter(Boolean)
    },
  },
  methods: {
    addFiles(files) {
      if (!this.value.find(({ path }) => path === this.path))
        this.value.push({ path: this.path, files: [], acl: [] })

      this.valueComp = this.valueComp.map(i => {
        if (this.path === i.path) i.files = [...files]
        return i
      })
    },
    getLastPath(path) {
      return ExtractString(path, /[^\/]+$/, { group: 0 })
    },
    getLocation(path) {
      return path.replace(/[^\/]+$/, '')
    },
    navigate(directory) {
      if (this.edit === directory.path) return
      this.$emit('update:path', directory.path)
    },
    onAccessChange(value, target, type) {
      switch (type) {
        case 'directory':
          const targets = this.value.filter(v => v.path.startsWith(target.path))
          return targets.forEach(t => (t.acl = !value ? [] : [value]))
        case 'file':
          target.acl = !value ? [] : [value]
      }

      // this.$emit('input', this.value)
    },
    getAccess(target, type) {
      switch (type) {
        case 'directory':
          const targets = this.value.filter(v => v.path.startsWith(target.path))
          const uniq = Array.from(new Set(targets.flatMap(v => v.acl)))
          if (
            targets.some(t => !(t.acl || []).length) ||
            uniq.length === (this.accessGroups || []).length
          )
            return []

          return uniq
        case 'file':
          return target.acl
      }
    },
    async preTransform(e) {
      if (e instanceof DragEvent) {
        let items = Array.from(e.dataTransfer.items)
        let files = []
        let map = {}

        await Promise.all(
          items.map(async cur => {
            let item = cur.webkitGetAsEntry()

            let fileHandler = async item => {
              let file = await new Promise((resolve, reject) => item.file(resolve, reject))

              // file.name.replace('\/', '')
              if (Blacklist.includes(file.name)) return

              files.push(file)

              let folder = this.getLocation(item.fullPath.slice(1)).slice(0, -1)
              if (folder in map) map[folder].push(files.length - 1)
              else map[folder] = [files.length - 1]
            }

            let directoryHandler = async dir => {
              let reader = dir.createReader()
              let readEntries = async () =>
                await new Promise((resolve, reject) => reader.readEntries(resolve, reject))
              let entries = await readEntries()

              while (entries.length) {
                await Promise.all(
                  Array.from(entries).map(entry => {
                    if (entry.isDirectory) return directoryHandler(entry)
                    return fileHandler(entry)
                  }),
                )

                entries = await readEntries()
              }
            }

            if (item.isDirectory) await directoryHandler(item)
          }),
        )

        this.directoryUploadMap = map

        return files
      } else {
        let files = Array.from(e.target.files).filter(f => !Blacklist.includes(f.name))
        this.directoryUploadMap = files.reduce((res, file, index) => {
          let folder = this.getLocation(file.webkitRelativePath).slice(0, -1)
          if (folder in res) res[folder].push(index)
          else res[folder] = [index]
          return res
        }, {})

        return files
      }
    },
    onDirectoryUpload(ev) {
      let directoryMap = {}

      ev.forEach((e, i) => {
        let directory = Object.entries(this.directoryUploadMap).find(([path, indices]) =>
          indices.includes(i),
        )[0]

        if (directory in directoryMap) directoryMap[directory].files.push(e)
        else
          directoryMap[directory] = {
            path: this.path ? `${this.path}/${directory}` : directory,
            files: [e],
            acl: [],
          }
      })

      this.$emit('input', [...this.value, ...Object.values(directoryMap)])
    },
    i18n(key) {
      if (!this.$t) return key
      return this.$t(key) || key
    },
  },
  created() {
    this.$bus.$on('add:folder', ev => (this.edit = ev))
  },
  mounted() {
    if (this.readonly) return
    let input = this.$refs.directories.$el.querySelector('input[type=file]')
    if (!input) return
    input.setAttribute('directory', true)
    input.setAttribute('webkitDirectory', true)
  },
  components: {
    FileComponent,
    Icon,
    ButtonComponent,
    DropdownComponent,
    AccessComponent,
  },
}
</script>

<style lang="scss">
.k-directory-render {
  min-height: 220px;
  max-height: 600px;
  overflow-y: auto;
  overflow-x: auto;

  display: flex;
  gap: 0.5rem;
  flex-direction: column;

  &__directory {
    display: flex;
    gap: 0.5rem;
    flex-direction: column;

    &-item {
      cursor: pointer;
      line-height: 1;
      position: relative;
      display: flex;
      align-items: center;
      gap: 1em;
      padding: 1rem;
      border: 1px solid var(--vue-elder-border-color, #eaeaea);
      border-radius: var(--vue-elder-border-radius, 3px);
      background-color: white;
      svg path {
        opacity: 0.9 !important;
      }
    }

    &-icon {
      font-size: 1.5em;
      opacity: 0.5;
    }

    &-name {
      font: inherit;
      font-weight: bold;
      transition: background-color 150ms ease-out;
      color: inherit !important;
      border: none;
      border-radius: var(--vue-elder-border-radius, 3px);
      background-color: transparent;
    }
  }

  &__files {
    display: flex;
    flex-direction: column;
    margin-top: 0.5rem;
  }

  &__uploads {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  .elder-button__icon {
    padding: 0.5em 0.75em !important;
  }
}
</style>
